/**
 * FieldRow — Carnaf design system v1.0
 * Spec: docs/strategy/car-as-place/design-system-2026-05-22/04-components.md § 5
 *
 * Used on the OCR review screen and KB editor list. One row per extracted
 * field with inline editing. Low-confidence variant adds a 3px amber left
 * border + "please confirm" nudge. No numeric confidence percentage — the
 * 87% problem (no one knows what 87% means).
 */

import { useState } from 'react';
import type { ReactNode } from 'react';

interface FieldRowProps {
  label: string;
  value: string;
  onChange?: (next: string) => void;
  /** Show as low-confidence: amber border + nudge text. */
  lowConfidence?: boolean;
  /** Custom nudge text; defaults to "נא לאשר". */
  nudge?: ReactNode;
  /** Render with tabular-nums + dir="ltr" for plates/VINs/phones. */
  tabular?: boolean;
  /** Render the value read-only (used in document view; tap to enter edit). */
  readOnly?: boolean;
  placeholder?: string;
}

export function FieldRow({
  label,
  value,
  onChange,
  lowConfidence = false,
  nudge = 'נא לאשר',
  tabular = false,
  readOnly = false,
  placeholder,
}: FieldRowProps): JSX.Element {
  const [internal, setInternal] = useState(value);
  // Sync external value changes
  if (internal !== value && !document.activeElement?.tagName?.match(/INPUT/i)) {
    setInternal(value);
  }

  const containerClasses = lowConfidence
    ? 'bg-carnaf-amber-soft rounded-md p-3 border-r-[3px] border-carnaf-amber'
    : 'bg-carnaf-paper-elevated rounded-md p-3';

  return (
    <div className={containerClasses}>
      <div className="flex items-baseline justify-between mb-1.5">
        <label className="text-body-sm font-medium text-carnaf-ink-muted">{label}</label>
        {lowConfidence && <span className="text-caption font-medium text-carnaf-amber">⚠ {nudge}</span>}
      </div>
      <input
        type="text"
        dir={tabular ? 'ltr' : undefined}
        value={internal}
        onChange={(e) => {
          setInternal(e.target.value);
          onChange?.(e.target.value);
        }}
        placeholder={placeholder}
        readOnly={readOnly}
        className={[
          'w-full px-3 py-2 rounded-md bg-white text-body',
          'border border-carnaf-line',
          'focus:outline-none focus:ring-2 focus:ring-carnaf-teal-soft focus:border-carnaf-teal',
          'read-only:bg-carnaf-paper-sunk',
          tabular ? 'tabular text-end' : '',
        ].join(' ')}
        style={{ minHeight: 44 }}
      />
    </div>
  );
}
